import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getApiUrl } from "../../../../config/api";
import { PublicPageShell, t } from "../shared/trendReportShared";

// Where the "Resubscribe" button in the unsubscribe notice email lands. Public.
// Nothing happens until the button here is pressed: corporate mail scanners
// open links in incoming mail, and a page that resubscribed on load would undo
// every unsubscribe whose notice got scanned. Once done, it warns that an
// unsubscribe the reader didn't make means someone else has a copy of their
// brief - typically one they forwarded.
//
// step: "ask" -> "done", or "used" / "expired" / "error".

const PRIMARY =
  "w-full p-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-bold rounded-md transition-colors duration-200";

const Title = ({ children }) => (
  <h1 className="text-xl font-bold text-gray-900 mb-3">{children}</h1>
);
const Body = ({ children }) => (
  <p className="text-sm text-gray-600 leading-relaxed mb-4">{children}</p>
);

export default function ResubscribePage() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const lang =
    new URLSearchParams(location.search).get("lang") === "zh" ? "zh" : "en";
  const setLang = (next) =>
    navigate({ search: `?lang=${next}` }, { replace: true });

  const [step, setStep] = useState("ask");
  const [busy, setBusy] = useState(false);

  const onResubscribe = async () => {
    setBusy(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/v2/trend-reports/resubscribe/${encodeURIComponent(token)}`,
        { method: "POST", headers: { "Content-Type": "application/json" } },
      );
      const data = await res.json().catch(() => ({}));
      if (data.success) setStep("done");
      else if (data.expired) setStep("expired");
      else if (data.not_found) setStep("used");
      else setStep("error");
    } catch {
      setStep("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicPageShell lang={lang} onLang={setLang}>
      {step === "ask" && (
        <div className="flex flex-col">
          <Title>{t("resubTitle", lang)}</Title>
          <Body>{t("resubIntro", lang)}</Body>
          <button type="button" disabled={busy} onClick={onResubscribe} className={PRIMARY}>
            {busy ? t("resubWorking", lang) : t("resubButton", lang)}
          </button>
        </div>
      )}

      {step === "done" && (
        <div>
          <Title>{t("resubDoneTitle", lang)}</Title>
          <Body>{t("resubDoneBody", lang)}</Body>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900 mb-1">
              {t("resubHeadsUpTitle", lang)}
            </p>
            <p className="text-sm text-amber-800 leading-relaxed">
              {t("resubHeadsUpBody", lang)}
            </p>
          </div>
        </div>
      )}

      {step === "used" && (
        <div>
          <Title>{t("pubInvalidTitle", lang)}</Title>
          <Body>{t("resubUsedBody", lang)}</Body>
        </div>
      )}

      {step === "expired" && (
        <div>
          <Title>{t("resubExpiredTitle", lang)}</Title>
          <Body>{t("resubExpiredBody", lang)}</Body>
        </div>
      )}

      {step === "error" && (
        <div className="flex flex-col">
          <Title>{t("pubErrorTitle", lang)}</Title>
          <Body>{t("pubErrorBody", lang)}</Body>
          <button type="button" onClick={() => setStep("ask")} className={PRIMARY}>
            {t("pubBack", lang)}
          </button>
        </div>
      )}
    </PublicPageShell>
  );
}
